// biome-ignore-all lint/security/noDangerouslySetInnerHtml: help previews render admin-authored text through richTextDisplayHtml (sanitizeRichText whitelist).
// =============================================================================
// HelpContentAdmin — edit the contextual help texts (ⓘ bubbles)
// =============================================================================
// Renders: a list of help keys with their bodies. Each row is editable.
//          Stored in the `help_content` table; surfaced via HelpButton
//          (in ChildSections.jsx).
//
// The key→label→tab catalogue lives in services/helpCatalog (single-sourced so
// the Resources "link to a help item" picker shows the same labels).
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { listHelpContent, upsertHelpContent } from "../../data/helpContent";
import { HELP_CATALOG, HELP_TABS, helpTabOf } from "../../services/helpCatalog";
import { richTextDisplayHtml } from "../../services/richText";
import { RichTextEditorModal } from "./RichTextEditorModal.jsx";

export function HelpContentAdmin() {
  const [tab, setTab] = React.useState("general");
  const [rows, setRows] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [editKey, setEditKey] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(function () {
    listHelpContent().then(function (rows) {
      var map = {};
      (rows || []).forEach(function (r) {
        map[r.key] = r;
      });
      setRows(map);
      setLoading(false);
    });
  }, []);

  function startEdit(key) {
    setEditKey(key);
  }
  async function save(html) {
    setSaving(true);
    await upsertHelpContent({
      key: editKey,
      text: html,
      updated_at: new Date().toISOString(),
    });
    setRows(function (prev) {
      return Object.assign({}, prev, { [editKey]: { key: editKey, text: html } });
    });
    setSaving(false);
    setEditKey(null);
  }

  if (loading) return <p className="text-zephyr-muted">Loading...</p>;
  return (
    <div>
      <h2 className="m-0 mb-1 text-[18px] text-zephyr-text">Help Content</h2>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        Edit the help text shown to shelter users via the (?) buttons and the welcome guide.
      </p>
      <div className="flex gap-1 mb-4 border-b border-zephyr-border">
        {HELP_TABS.map(function (t) {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={function () {
                setTab(t.id);
              }}
              className="bg-transparent border-none py-2 px-4 text-[13px] cursor-pointer font-semibold"
              style={{
                fontFamily: "inherit",
                color: active ? "#00695C" : "#607D79",
                borderBottom: active ? "2px solid #00695C" : "2px solid transparent",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {HELP_CATALOG.filter(function (h) {
        return helpTabOf(h.key) === tab;
      }).map(function (h) {
        const hasContent = !!(rows[h.key] && rows[h.key].text);
        return (
          <div
            key={h.key}
            className="border border-zephyr-border rounded-[8px] mb-[10px] py-3 px-4 bg-white"
          >
            <div className="flex justify-between items-center">
              <div>
                <span className="font-bold text-[14px] text-zephyr-text">{h.label}</span>
                <span className="text-xs text-zephyr-muted ml-[10px]">{h.desc}</span>
                {!hasContent && <span className="text-[11px] text-[#E65100] ml-2">(empty)</span>}
              </div>
              <button
                onClick={function () {
                  startEdit(h.key);
                }}
                className="bg-transparent border border-zephyr-teal rounded-[6px] py-1 px-3 text-xs text-zephyr-teal cursor-pointer font-semibold"
                style={{ fontFamily: "inherit" }}
              >
                Edit
              </button>
            </div>
            {hasContent && (
              <div
                className="mt-2 text-xs text-zephyr-muted max-h-[60px] overflow-hidden"
                style={{ whiteSpace: "pre-wrap" }}
                dangerouslySetInnerHTML={{ __html: richTextDisplayHtml(rows[h.key].text) }}
              />
            )}
          </div>
        );
      })}
      {editKey && (
        <RichTextEditorModal
          title={`Edit — ${
            HELP_CATALOG.find(function (h) {
              return h.key === editKey;
            })?.label || editKey
          }`}
          initialHtml={rows[editKey] ? rows[editKey].text : ""}
          hint={<span>Bold, underline and font size are supported.</span>}
          onSave={save}
          onCancel={function () {
            setEditKey(null);
          }}
        />
      )}
      {saving && <div className="text-[12px] text-zephyr-muted mt-1">Saving...</div>}
    </div>
  );
}
